import React, { useState, useEffect } from "react";
import {
    Box,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    Tab,
    Select,
    MenuItem,
} from "@mui/material";

import { useNavigate } from "react-router-dom";
import SearchBar from "../../components/SearchBar";
import dayjs from "dayjs";
import { TICKETS_PRIORITY } from "../../utils/enums";
import { SupportTicket } from "../../types/General";
import Loader from "../../constants/Loader";
import CustomPagination from "../../components/CustomPagination";
import WarnModal from "../../Modals/warnmodal";
import { useGetTicketsQuery, useDeleteTicketMutation } from "../../service/main";
import toast from "react-hot-toast";
import { isValidInput } from "../../utils/validations";


const ReadMore = ({ text, maxLength = 100 }: { text: string; maxLength?: number }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    if (!text) return <>-</>;
    if (text.length <= maxLength) {
        return <span style={{ whiteSpace: "normal", wordBreak: "break-word" }}>{text}</span>;
    }

    return (
        <span style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
            {isExpanded ? text : `${text.substring(0, maxLength)}...`}
            <button
                type="button"
                onClick={() => setIsExpanded(!isExpanded)}
                style={{
                    background: "none",
                    border: "none",
                    color: "#D3B88C",
                    cursor: "pointer",
                    padding: 0,
                    marginLeft: "5px",
                    fontWeight: 600,
                    fontSize: "12px",
                    textDecoration: "underline",
                }}
            >
                {isExpanded ? "See Less" : "See More"}
            </button>
        </span>
    );
};

const ViewAdminSupport = () => {
    const navigate = useNavigate();
    const [page, setPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
    const [subTab, setSubTab] = useState(0);
    const [openDelete, setOpenDelete] = useState(false);
    const [priority, setPriority] = useState("all");
    const [selectedId, setSelectedId] = useState<string>("");

    const { data, isLoading, isFetching, refetch } = useGetTicketsQuery({
        page,
        limit: 10,
        ...(debouncedSearchTerm ? { search: debouncedSearchTerm } : {}),
        ...(priority !== "all" ? { priority: Number(priority) } : {}),
        status: subTab === 0 ? 1 : 3
    });
    const ticketData: SupportTicket[] = data?.data?.list;
    const count = data?.data?.total || 0;

    useEffect(() => {
        setPage(1);
    }, [subTab, debouncedSearchTerm, priority]);

    const subTabs: ("Pending" | "Resolved")[] = ["Pending", "Resolved"];

    const handleSubTabChange = (
        event: React.SyntheticEvent,
        newValue: number,
    ) => {
        setSubTab(newValue);
    };

    const [deleteTicket, { isLoading: isDeleting }] = useDeleteTicketMutation();

    const handleDelete = async () => {
        try {
            const response = await deleteTicket({ id: selectedId }).unwrap();
            if (response.statusCode === 200) {
                toast.success(response.message || "Ticket deleted successfully");
                refetch();
            } else {
                toast.error(response.message || "Something went wrong");
            }
        } catch (error: any) {
            toast.error(error?.data?.message || "An error occurred");
        }
    };

    return (
        <>
            <Loader isLoad={isLoading || isFetching || isDeleting} />
            <div className="main_title">
                <h1>Admin Support Management View</h1>
                <p>
                    <button onClick={() => navigate("/dashboard")}>Dashboard</button> -
                    <button onClick={() => navigate("/admin-support")}>
                        Admin Support Management
                    </button>{" "}
                    - View
                </p>
            </div>

            <Box className="custom_tabs1 mt_20">
                <Tabs
                    value={subTab}
                    onChange={handleSubTabChange}
                    className="custom_tabs_links"
                >
                    {subTabs.map((label, index) => (
                        <Tab key={index} label={label} />
                    ))}
                </Tabs>
            </Box>

            <div className="cards">
                <div className="table_header">
                    <div className="left_s support_search">
                        <SearchBar
                            value={searchTerm}
                            onChange={(e: any) => isValidInput(e.target.value) && setSearchTerm(e.target.value)}
                            onCross={() => {
                                setSearchTerm("");
                                setDebouncedSearchTerm("");
                                setPage(1);
                            }}
                            searchTerm={searchTerm}
                            setDebouncedSearchTerm={setDebouncedSearchTerm}
                            placeholder="Search..."
                        />
                        <div className="form">
                            <Select
                                fullWidth
                                value={priority}
                                onChange={(e) => {
                                    setPriority(e.target.value as string);
                                    setPage(1);
                                }}
                            >
                                <MenuItem value="all">All Priorities</MenuItem>
                                <MenuItem value="1">Low</MenuItem>
                                <MenuItem value="2">Medium</MenuItem>
                                <MenuItem value="3">High</MenuItem>
                            </Select>
                        </div>
                    </div>
                </div>

                <TableContainer className="table_container mt_20">
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Sr. No.</TableCell>
                                <TableCell>Title</TableCell>
                                <TableCell>Description</TableCell>
                                <TableCell>Category</TableCell>
                                <TableCell>Priority</TableCell>
                                <TableCell>Submitted Date</TableCell>

                                {subTabs[subTab] === "Resolved" && (
                                    <TableCell>Admin Reply</TableCell>
                                )}

                                <TableCell>Action</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {ticketData?.length
                                ? ticketData?.map((row, index) => (
                                    <TableRow key={row.id} hover>
                                        <TableCell>{index + 1}</TableCell>
                                        <TableCell>{row.subject}</TableCell>
                                        <TableCell style={{ whiteSpace: "normal", wordBreak: "break-word", maxWidth: "250px" }}>
                                            <ReadMore text={row.description} />
                                        </TableCell>
                                        <TableCell>{row.category}</TableCell>
                                        <TableCell>
                                            {row?.priority === String(TICKETS_PRIORITY.LOW)
                                                ? "Low"
                                                : row?.priority === String(TICKETS_PRIORITY.HIGH)
                                                    ? "High"
                                                    : row?.priority === String(TICKETS_PRIORITY.MEDIUM)
                                                        ? "Medium"
                                                        : "-"}
                                        </TableCell>
                                        <TableCell>
                                            {row?.createdAt
                                                ? dayjs(row?.createdAt).format("DD MMM YYYY")
                                                : "-"}
                                        </TableCell>

                                        {subTabs[subTab] === "Resolved" && (
                                            <TableCell>{"-"}</TableCell>
                                        )}

                                        <TableCell>
                                            <div className="table_actions v2">
                                                <Box>
                                                    <IconButton
                                                        onClick={() => {
                                                            setSelectedId(row.id);
                                                            setOpenDelete(true);
                                                        }}>
                                                        <img
                                                            src="/static/images/trash2_icon.svg"
                                                            alt="Delete"
                                                        />
                                                    </IconButton>
                                                </Box>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                                : null}

                            {ticketData?.length === 0 && (
                                <TableRow>
                                    <TableCell
                                        colSpan={subTabs[subTab] === "Resolved" ? 7 : 6}
                                        align="center"
                                    >
                                        No records found
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>
            {count > 0 ? (
                <CustomPagination
                    currentPage={page - 1}
                    count={count}
                    pageSize={10}
                    onPageChange={(page) => setPage(page + 1)}
                />
            ) : null}

            <WarnModal
                open={openDelete}
                setOpen={setOpenDelete}
                handleDelete={handleDelete}
                name="ticket"
            />
        </>
    );
};

export default ViewAdminSupport;
